import { createElement, useMemo, useRef, useEffect, useState, Fragment } from 'react';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { TabBarVariant, TabProps, TabState } from '.';
import { getTabTokens, getIconButtonTokens } from '.';
import { postfixTestId } from '../../../util/testId';
import { Icon } from '../../Icon';
import { Text } from '../../Text';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { InvisibleText } from '../../InvisibleText';
import { Input, InputChangeEvent, InputCommitEvent } from '../../Input';
import { Popover } from '../../Popover';
import { ItemLocation, Menu, MenuSection } from '../../Menu';
import { FocusLocation } from '../../Menu/__internal__';
import { useToggleMenu } from '../../MenuButton';
import { CloseTab, OverflowLite } from '@sas/react-svg';
import { Indicator } from '../../Indicator';
import { ContextMenu } from '../../ContextMenu';
import { withLabels } from '../../withLabels';
import { isNullish } from '@sas/nova-core/typeguards';
import bundle from '../NovaComponents-Tab-gui-icu.properties';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useKeyboardShortcuts } from '../../KeyboardShortcuts';
import { useKeyboardTooltip } from '../../Settings';
import { useTruncationTooltip } from '../../../hooks/useTruncationTooltip';

import cx from 'classnames';
import cn from './Tab.css.json';
import './Tab.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

const useLabelId = createUseHtmlId('TabBar-label');
const useIconId = createUseHtmlId('TabBar-icon');
const useTabStateId = createUseHtmlId('Tab-state');
const useCloseTabId = createUseHtmlId('Tab-close');
const useNonMovableTabId = createUseHtmlId('Tab-non-movable');

const InputWithLabel = withLabels(Input);

/**
 * Don't use Tab outside of Tabbar, pleae use the Tabbar instead.
 */
export const Tab = function (props: TabProps) {
  const localize = useTextLocalizer();
  const refTab = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuBtnRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const selected = props.selected;
  const alternate = props.visualStyle === 'alternative';
  const standardSmall = props.visualStyle === 'standard-small';
  const variant = props.variant ?? TabBarVariant.Standard;
  const orientation = props.orientation ?? 'horizontal';
  const hidden = props.hidden;
  const state = props.state ? props.state : hidden ? 'hidden' : undefined;
  const count = props.count ?? undefined;
  const hideTabMenuButton = props.hideTabMenuButton;
  const dt = useDesignTokens();
  const { getActionsByKeyEvent } = useKeyboardShortcuts();

  const stateArray: TabState[] = !isNullish(state) && !Array.isArray(state) ? [state] : state ?? [];

  const [isInEditMode, setEditMode] = useState(false);
  const [editedLabel, setEditedLabel] = useState(props.label);
  const [isEditedLabelPopoverOpen, setIsEditedLabelPopoverOpen] = useState(false);
  const [defaultFocusLocation, setDefaultFocusLocation] = useState<FocusLocation>();
  const labelValue = props.label ?? '';
  const minTabWidth = Number(localize('tabBar.minimumCharactersTab.lcl'));
  const renameMenuItemtext = localize('tab.contextMenu.Rename.label');
  const closeTabMenuItemText = props.closeTabMenuItemText ?? localize('tab.closeTabMenuItem.label');
  const closeAllTabMenuItemText =
    props.closeAllTabMenuItemText ?? localize('tab.closeAllTabMenuItem.label');
  const renameMenuItemPosition = props.renameMenuItemPosition ?? 0;
  const showRenameMenuOption = props.showRenameMenuOption ?? true;
  const showCloseMenuOption = props.showCloseMenuOption ?? true;
  const hideCloseAllTabOption = props.hideCloseAllTabOption;
  const showContextMenu = props.showContextMenu;
  const setTextRef = useTruncationTooltip(undefined, textRef);

  const cssVars = useMemo(() => {
    const tokenOverrides = props.tokenOverrides;

    return renderInlineCssVars(
      {
        ...getTabTokens(dt, { selected, alternate, standardSmall, variant, orientation }),

        'spacing-tab-items': dt.spacing.insetSquish.m.padding,
        'item-font-size': dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m,
        'line-height-tab-items': dt.fontNova.lineHeight,
        // If overrides are provided don't provide a hover-bg. The consumer can handle that if they want to.

        ...(tokenOverrides ? { 'hover-bg': undefined } : undefined),
        ...tokenOverrides?.(dt, { selected, alternate, standardSmall, variant }),
        ...getIconButtonTokens(dt),
        'font-size-s': dt.enableNovaTokens ? dt.fontNova.size.s : dt.font.size.s,
        'min-width': `${minTabWidth}ch`,
      },
      'tab'
    );
  }, [
    dt,
    selected,
    alternate,
    standardSmall,
    variant,
    orientation,
    minTabWidth,
    props.tokenOverrides,
  ]);

  const labelId = useLabelId();
  const iconId = useIconId();
  const stateTabIds = useTabStateId(stateArray.length);
  const closeTabId = useCloseTabId();
  const nonMovableTabId = useNonMovableTabId();
  const ariaLabelledBy: string[] = [];

  if (props.icon) {
    ariaLabelledBy.push(iconId);
  }

  if (props.label) {
    ariaLabelledBy.push(labelId);
  }

  const ariaDescribedBy: string[] = [];
  if (stateArray.length > 0) {
    ariaDescribedBy.splice(0, 0, ...stateTabIds);
  }
  if (props.closable) {
    ariaDescribedBy.push(closeTabId);
  }

  if (!props.allowDrag && props.movableTab) {
    ariaDescribedBy.push(nonMovableTabId);
  }

  const setTabMovableKeyboardTooltipRef = useKeyboardTooltip({
    tooltip: localize('tab.movable.tooltip.label'),
  });

  const setTabContextMenuTooltipRef = useKeyboardTooltip({
    tooltip: localize('tab.contextMenuMessage.tooltip.label'),
  });

  const setKeyboardTooltipRef = useKeyboardTooltip({
    tooltip:
      props.closeTabButtonkeyboardTooltip ??
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      `${localize('tab.contextMenuMessage.tooltip.label')}\n${localize(
        'tab.closeButtonMessage.aria'
      )}`,
  });

  const getMenuSections = () => {
    const itemMenuSections = [...(props.menuProps?.menuSections ?? [])];
    if (props.editable && showRenameMenuOption && props.label && props.label.length > 0) {
      const index =
        renameMenuItemPosition < 0 || itemMenuSections.length <= renameMenuItemPosition
          ? 0
          : renameMenuItemPosition;

      itemMenuSections.splice(index, 0, {
        items: [{ text: renameMenuItemtext }],
      });
    }

    if (props.closable && showCloseMenuOption) {
      itemMenuSections.push({
        items: [{ text: closeTabMenuItemText }],
      });
    }

    if (props.closable && hideCloseAllTabOption) {
      itemMenuSections.push({
        items: [{ text: closeAllTabMenuItemText }],
      });
    }
    return itemMenuSections as ReadonlyArray<MenuSection>;
  };

  const onItemSelected = (location: ItemLocation[]) => {
    const selectedMenuText = menuSections?.[location[0][0]].items[location[0][1]].text;
    if (props.editable && location[0][0] === renameMenuItemPosition) {
      setIsEditedLabelPopoverOpen(!isEditedLabelPopoverOpen);
      setEditMode(!!props.editable);
    } else if (selectedMenuText === closeAllTabMenuItemText) {
      props.onCloseAll?.();
    } else if (props.closable && selectedMenuText === closeTabMenuItemText && !props.menuProps) {
      props.onClose?.();
    } else if (props.menuProps) {
      props.menuProps?.onMenuItemSelect(location, menuSections);
    }
  };

  const { handleDismiss, handlePress, setMenuIsOpen, menuIsOpen } = useToggleMenu({
    disabled: false,
    onDismiss: () => {
      refTab.current?.focus();
      setMenuIsOpen(false);
    },
    onOpen: () => {
      if (props.menuProps && props.menuProps.onOpen) {
        props.menuProps.onOpen();
      }
      setDefaultFocusLocation(undefined);
    },
  });

  useEffect(() => {
    const tabContextMenu: HTMLElement | null = document.querySelector(
      "[data-testid^='tab-contextMenu']"
    );

    if (props.focused) {
      if (isInEditMode) {
        inputRef.current?.focus();
      } else if (tabContextMenu === null) {
        refTab.current?.focus();
      }
    }
  }, [props.focused, isInEditMode]);

  useEffect(() => {
    setEditedLabel(undefined);
  }, [isInEditMode]);

  function isMenuButtonClicked(e: React.MouseEvent) {
    return e.target === menuBtnRef.current || menuBtnRef.current?.contains(e.target as Node);
  }
  const menuSections = getMenuSections();

  const showStateIndicator = Array.isArray(state)
    ? state?.includes('hidden') || state?.includes('processing')
    : state === 'processing' || state === 'hidden';

  const renderTab = () => {
    return (
      <Fragment>
        <div
          id={props.id}
          data-altid={props.altId}
          className={cx(
            cn.tab,
            props.menuProps && !hideTabMenuButton && cn.withMenu,
            !props.label && cn.iconOnly,
            props.selected && !props.disabled && cn.selected,
            alternate && cn.alternate,
            standardSmall && cn.standardSmall,
            props.closable && cn.withClosable,
            props.disabled && cn.disabled,
            props.allowDrag && cn.dragableTab,
            props.className
          )}
          style={{
            ...cssVars,
            ...props.style,
          }}
          onClick={(e) =>
            !e.isDefaultPrevented() &&
            !isMenuButtonClicked(e) &&
            !props.disabled &&
            props.onPress?.()
          }
          onDoubleClick={() => {
            if (
              props.editable &&
              !props.disabled &&
              !isEditedLabelPopoverOpen &&
              props.label?.length
            ) {
              setIsEditedLabelPopoverOpen(true);
              setEditMode(true);
            }
          }}
          onKeyDown={(e) => {
            const { actions } = getActionsByKeyEvent(e.nativeEvent);
            // Do not implement stopPropagation in case of landmark
            if (e.target !== e.currentTarget && !actions.includes('openLandmarks')) {
              e.stopPropagation();
            }

            if (e.key === 'Delete' && !props.disabled && props.closable) {
              props.onClose?.();
            }
          }}
          data-testid={props.testId}
          data-sasrc-tablocation={props.location || 'top'}
          tabIndex={props.tabIndex}
          ref={(element: HTMLDivElement) => {
            refTab.current = element;
            if (props.allowDrag) {
              setTextRef(element);
            }
            if (props.editable || props.closable || props.menuProps)
              setTabContextMenuTooltipRef(element);
            if (props.closable) setKeyboardTooltipRef(element);
            if (props.allowDrag && !props.disabled) setTabMovableKeyboardTooltipRef(element);
          }}
          onFocus={props.onFocus}
          onBlur={(e) => {
            if (menuIsOpen) {
              e.stopPropagation();
            }
          }}
          role="tab"
          aria-selected={props.selected}
          aria-labelledby={ariaLabelledBy.join(' ')}
          aria-disabled={!!props.disabled}
          aria-controls={props.ariaControls}
          aria-describedby={ariaDescribedBy.join(' ')}
        >
          <div className={cn.tabInner}>
            {props.icon && !showStateIndicator && (
              <Icon
                {...props.icon}
                className={cx(cn.icon, props.icon.className)}
                // Overrides the `--icon-fg` custom property so tab icons
                // always inherit the `--tab-fg` color.
                tokens={() => {
                  return { fg: props.disabled ? 'var(--tab-disabled-fg)' : 'var(--tab-fg)' };
                }}
                id={iconId}
                ariaLabel=""
              />
            )}
            {props.label && props.label.length > 0 && (
              <Fragment>
                <Text
                  testId={postfixTestId(props.testId, 'text')}
                  id={labelId}
                  aria-hidden
                  wrapText={false}
                  className={cx(
                    cn.text,
                    (state === 'alert' || state === 'ready' || state === 'unsaved') && cn.fillwidth
                  )}
                  ref={textRef}
                >
                  {props.label}
                </Text>

                <Popover
                  referenceElementRef={refTab}
                  isOpen={isEditedLabelPopoverOpen}
                  onClose={() => setIsEditedLabelPopoverOpen(false)}
                  onEscape={() => refTab.current?.focus()}
                  role="dialog"
                  ariaLabel={localize('tab.popover.ariaLabel.aria')}
                  onOutsideClick={() => {
                    setEditMode(false);
                    setEditedLabel(editedLabel === '' ? undefined : editedLabel);
                    props.onLabelChange?.(editedLabel ?? '', 'Commit'); // eslint-disable-line @sas/i18n/jsx-to-localize
                    refTab.current?.focus();
                  }}
                >
                  <InputWithLabel
                    testId={postfixTestId(props.testId, 'input')}
                    label={localize('tab.popover.inputLabelName.label')}
                    labelPosition="left"
                    fieldSuffixMinWidth={0}
                    inputStyle={{ maxWidth: '25rem' }}
                    fieldWidth={'16rem'}
                    fieldMinWidth={'3rem'}
                    ref={inputRef}
                    value={isInEditMode ? editedLabel ?? labelValue : labelValue}
                    onChange={(e: InputChangeEvent) => {
                      setEditedLabel(e.value);
                      props.onLabelChange?.(e.value, 'Change'); // eslint-disable-line @sas/i18n/jsx-to-localize
                    }}
                    onCommit={(e: InputCommitEvent) => {
                      setEditedLabel(e.value === '' ? labelValue : e.value);
                      props.onLabelChange?.(e.value, 'Commit'); // eslint-disable-line @sas/i18n/jsx-to-localize
                      setEditMode(false);
                      setIsEditedLabelPopoverOpen(false);
                    }}
                    onBlur={() => {
                      setEditedLabel(undefined);
                      setEditMode(false);
                      setIsEditedLabelPopoverOpen(false);
                    }}
                    onCancel={() => {
                      setEditedLabel(undefined);
                      props.onLabelChange?.(labelValue ?? '', 'Cancel'); // eslint-disable-line @sas/i18n/jsx-to-localize
                      setEditMode(false);
                      setIsEditedLabelPopoverOpen(false);
                    }}
                    message={props.message}
                    status={props.status}
                  />
                </Popover>
              </Fragment>
            )}
            {stateArray.map((element, index) => {
              return (
                <Fragment key={element}>
                  <Indicator
                    key={element}
                    type={element}
                    count={element === 'count' ? count : undefined}
                    className={cx(
                      cn.indicator,
                      (element === 'hidden' || element === 'processing') && cn.placeStart,
                      orientation === 'vertical' &&
                        (element === 'alert' || element === 'ready' || element === 'unsaved') &&
                        cn.shiftStart,
                      orientation === 'vertical' &&
                        (element === 'count' || element === 'readOnly') &&
                        cn.sticky
                    )}
                    testId={postfixTestId(props.testId, `state-${element}`)}
                    tooltip={
                      element === 'hidden' ? localize('tab.hidden.ariaLabel.aria') : undefined
                    }
                  />
                  <InvisibleText id={stateTabIds[index]}>
                    {element === 'count'
                      ? localize('tab.count.ariaLabel.aria', { count: `${count}` })
                      : localize(`tab.${element}.ariaLabel.aria`)}
                  </InvisibleText>
                </Fragment>
              );
            })}
            {!isNullish(props.menuProps) && !hideTabMenuButton && (
              <Fragment>
                <span className={cn.iconButtonContainer}>
                  <Icon
                    icon={OverflowLite}
                    ref={menuBtnRef}
                    testId={postfixTestId(props.testId, 'menuBtn')}
                    tooltip={props.menuButtonTooltip ?? localize('tab.menuButton.tooltip.label')}
                    ariaHidden
                    ariaLabel={''}
                    tabIndex={-1}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={handlePress}
                    disabled={props.disabled}
                    className={cx(cn.iconButton, menuIsOpen && cn.open)}
                    tokens={() => {
                      return {
                        fg: props.disabled ? 'var(--tab-disabled-fg)' : 'var(--tab-fg)',
                      };
                    }}
                  />
                </span>
                <Menu
                  isOpen={menuIsOpen}
                  sections={menuSections}
                  onItemSelected={(location) => {
                    onItemSelected(location);
                    setMenuIsOpen(false);
                  }}
                  position={{ dockingElement: menuBtnRef.current }}
                  onDismiss={handleDismiss}
                  defaultFocusLocation={defaultFocusLocation}
                />
              </Fragment>
            )}
            {props.closable && (!props.menuProps || hideTabMenuButton) && (
              <Fragment>
                <InvisibleText id={closeTabId}>
                  {localize('tab.closeButtonMessage.aria')}
                </InvisibleText>
                <span className={cn.iconButtonContainer}>
                  <Icon
                    testId={postfixTestId(props.testId, 'closebutton')}
                    icon={CloseTab}
                    tooltip={
                      props.closeTabButtonTooltip ?? localize('tab.closeButton.tooltip.label')
                    }
                    disabled={props.disabled}
                    tabIndex={-1}
                    ariaHidden
                    onPress={() => {
                      props.onClose?.();
                    }}
                    className={cn.iconButton}
                    tokens={() => {
                      return {
                        fg: props.disabled ? 'var(--tab-disabled-fg)' : 'var(--tab-fg)',
                      };
                    }}
                  />
                </span>
              </Fragment>
            )}
          </div>
        </div>
        {!props.allowDrag && props.movableTab && (
          <InvisibleText id={nonMovableTabId}>
            {localize('tab.not.movable.message.aria')}
          </InvisibleText>
        )}
      </Fragment>
    );
  };

  return showContextMenu && !props.disabled && menuSections.length > 0 ? (
    <ContextMenu
      testId={'tab-contextMenu'}
      sections={menuSections}
      onItemSelected={(location) => onItemSelected(location)}
      onContextMenu={props.menuProps?.onOpen}
    >
      {renderTab()}
    </ContextMenu>
  ) : (
    renderTab()
  );
};
